/**
 * Zementarten als Matrix Zementart x Festigkeitsklasse mit Uebersicht I.-V. oben (D-06, Sketch 023
 * Variante C, Runde 7). Struktur (Gruppen, Kopfzeilen beim Klassenwechsel, Zwischen- und
 * Gruppensummen) kommt vollstaendig aus `data.table.recap`/`data.table.lines` -- keine eigene
 * Zementarten-Konfiguration im Frontend und bewusst ohne die Regelpruef-Spalten der anderen vier
 * Auswertungen (die Regel wird hier je Zelle statt je Zeile gefuehrt).
 */
import { type CSSProperties, type ReactNode } from 'react';
import { Card, Table } from '@mantine/core';
import { LockLegend } from './LockLegend';
import { PreviousValueCell } from './EvaluationValueCell';
import { cellColorVars } from './evaluationColorVars';
import {
  ChangeCell,
  RowLabel,
  TableCaption,
  TableCard,
  ValueCellWithMenu,
  type EvaluationTableSharedProps,
} from './RegionalEvaluationTable';
import { formatPercent } from './evaluationFormat';
import {
  DIVIDER_STYLE,
  GROUP_HEADER_TYPO,
  TABLE_CARD_STYLE,
  HEADER_GROUP_BG,
  HEADER_LABEL_BG,
  rowStyle,
} from '../ui/reportTable';
import rowClasses from '../ui/reportTable.module.css';
import type {
  ZementartenLine,
  ZementartenTable,
} from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

const LABEL_HEAD_STYLE: CSSProperties = {
  backgroundColor: HEADER_LABEL_BG,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

/** Gruppenzeile der Matrix (I. Normzement ...) wie die Gruppenzeile im Tabellenkopf. */
const GROUP_ROW_STYLE: CSSProperties = {
  backgroundColor: HEADER_GROUP_BG,
  textAlign: 'left',
  ...GROUP_HEADER_TYPO,
};

function renderMatrixLines(
  table: ZementartenTable,
  shared: EvaluationTableSharedProps
): ReactNode[] {
  const { data, onOpenBreakdown } = shared;
  const totalColumns = table.classColumns + 5;
  let currentClasses: string[] = [];

  return table.lines.map((line: ZementartenLine, index: number) => {
    if (line.kind === 'group') {
      return (
        <Table.Tr key={`group-${index}`}>
          <Table.Th colSpan={totalColumns} style={GROUP_ROW_STYLE}>
            {line.label}
          </Table.Th>
        </Table.Tr>
      );
    }

    if (line.kind === 'header') {
      currentClasses = line.classes;
      return (
        <Table.Tr key={`header-${index}`}>
          <Table.Th style={{ ...LABEL_HEAD_STYLE, textAlign: 'left' }}>
            Zementart
          </Table.Th>
          {line.classes.map((cls, i) => (
            <Table.Th key={i} style={LABEL_HEAD_STYLE}>
              {cls}
            </Table.Th>
          ))}
          <Table.Th style={{ ...LABEL_HEAD_STYLE, ...DIVIDER_STYLE }}>
            Summe {data.year}
          </Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>{data.year - 1}</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>Veränd. %</Table.Th>
          <Table.Th style={LABEL_HEAD_STYLE}>Anteil %</Table.Th>
        </Table.Tr>
      );
    }

    if (line.kind === 'type') {
      const sumCell = data.cells[line.sumCellKey];
      return (
        <Table.Tr
          key={line.rowKey}
          className={rowClasses.dataRow}
          style={rowStyle(false)}
        >
          <RowLabel
            breakdownKey={sumCell?.breakdownRowKey ?? null}
            onOpenBreakdown={onOpenBreakdown}
            title={line.longLabel}
          >
            {line.label}
          </RowLabel>
          {currentClasses.map((_, i) => {
            const cellKey = line.classCellKeys[i];
            if (!cellKey) {
              return <Table.Td key={i} />;
            }
            const cell = data.cells[cellKey];
            return (
              <ValueCellWithMenu
                key={cellKey}
                {...shared}
                cell={cell}
                unit="tonnes"
                zeroMuted
              />
            );
          })}
          <ValueCellWithMenu
            {...shared}
            cell={sumCell}
            unit="tonnes"
            bold
            style={DIVIDER_STYLE}
          />
          <PreviousValueCell
            value={sumCell.previousValue}
            previousLocked={sumCell.previousLocked}
            unit="tonnes"
          />
          <ChangeCell value={line.change} />
          <Table.Td className={classes.num}>
            {formatPercent(line.share)}
          </Table.Td>
        </Table.Tr>
      );
    }

    // subtotal | groupTotal
    const cell = data.cells[line.cellKey];
    const isGroupTotal = line.kind === 'groupTotal';
    return (
      <Table.Tr
        key={`${line.kind}-${index}`}
        className={rowClasses.dataRow}
        style={rowStyle(isGroupTotal)}
        fw={700}
      >
        <Table.Td colSpan={currentClasses.length + 1}>{line.label}</Table.Td>
        <ValueCellWithMenu
          {...shared}
          cell={cell}
          unit="tonnes"
          bold
          style={DIVIDER_STYLE}
        />
        <PreviousValueCell
          value={cell.previousValue}
          previousLocked={cell.previousLocked}
          unit="tonnes"
        />
        <ChangeCell value={line.change} />
        <Table.Td className={classes.num}>{formatPercent(line.share)}</Table.Td>
      </Table.Tr>
    );
  });
}

export function ZementartenMatrix(props: EvaluationTableSharedProps) {
  const { data } = props;
  const table = data.table;
  if (!table || table.layout !== 'zementarten') {
    return null;
  }

  return (
    <div style={cellColorVars}>
      <TableCaption caption={table.caption} data={data} />

      <Card
        padding={0}
        radius="md"
        withBorder
        maw={760}
        mb="lg"
        style={TABLE_CARD_STYLE}
      >
        <Table fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ ...LABEL_HEAD_STYLE, textAlign: 'left' }}>
                Übersicht
              </Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>{data.year}</Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>Anteil %</Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>{data.year - 1}</Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>Anteil %</Table.Th>
              <Table.Th style={LABEL_HEAD_STYLE}>Veränd. %</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {table.recap.map((entry) => {
              const cell = data.cells[entry.cellKey];
              return (
                <Table.Tr
                  key={entry.cellKey}
                  className={rowClasses.dataRow}
                  style={rowStyle(entry.grand)}
                  fw={entry.grand ? 700 : undefined}
                >
                  <Table.Td className={classes.labelCol}>
                    {entry.label}
                  </Table.Td>
                  <ValueCellWithMenu
                    {...props}
                    cell={cell}
                    unit="tonnes"
                    bold={entry.grand}
                  />
                  <Table.Td className={classes.num}>
                    {formatPercent(entry.share)}
                  </Table.Td>
                  <PreviousValueCell
                    value={cell.previousValue}
                    previousLocked={cell.previousLocked}
                    unit="tonnes"
                  />
                  <Table.Td className={classes.num}>
                    {formatPercent(entry.previousShare)}
                  </Table.Td>
                  <ChangeCell value={entry.change} />
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <TableCard>
        <div data-testid="evaluation-table-scroll" className={classes.scroll}>
          {/* D-04: 14 Spalten bei 1440 px -- enger als der Theme-Standard (11 px), sonst Querscrollen. */}
          <Table data-testid="evaluation-table" fz="sm" horizontalSpacing={5}>
            <Table.Tbody>{renderMatrixLines(table, props)}</Table.Tbody>
          </Table>
        </div>
      </TableCard>

      <LockLegend extra="Zementart anklicken: Meldung je Werk. Anteil bezogen auf IV. Gesamtmenge." />
    </div>
  );
}
